import { AdminAPI } from '@shared/types/api';

// Shared presentational pieces for the Feedback Triage surface. Components
// only (fast-refresh rule); non-component helpers stay module-private.

const DISPOSITION_STYLES: Record<AdminAPI.TriageDisposition, string> = {
  auto_answerable: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  needs_review: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  needs_action: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
  spam: 'bg-neutral-200 text-neutral-500 dark:bg-neutral-800 dark:text-neutral-400',
};

const DISPOSITION_LABELS: Record<AdminAPI.TriageDisposition, string> = {
  auto_answerable: 'auto-answerable',
  needs_review: 'needs review',
  needs_action: 'needs action',
  spam: 'spam',
};

const TICKET_STATUS_STYLES: Record<AdminAPI.TriageTicketStatus, string> = {
  new: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  classified: 'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300',
  awaiting_approval: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  waiting_on_user: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300',
  resolved: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  spam: 'bg-neutral-200 text-neutral-500 dark:bg-neutral-800 dark:text-neutral-400',
  merged: 'bg-neutral-200 text-neutral-500 dark:bg-neutral-800 dark:text-neutral-400',
};

export function DispositionChip({ disposition }: { disposition: AdminAPI.TriageDisposition | null }) {
  if (!disposition) return null;
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium whitespace-nowrap ${DISPOSITION_STYLES[disposition]}`}
    >
      {DISPOSITION_LABELS[disposition]}
    </span>
  );
}

export function TicketStatusChip({ status }: { status: AdminAPI.TriageTicketStatus }) {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium whitespace-nowrap ${TICKET_STATUS_STYLES[status]}`}
    >
      {status.replace(/_/g, ' ')}
    </span>
  );
}

export function CategoryChip({ category }: { category: string | null }) {
  if (!category) return null;
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium whitespace-nowrap bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300 border border-neutral-200 dark:border-neutral-700">
      {category}
    </span>
  );
}

/** High priority gets a red accent; low is muted; normal renders nothing. */
export function PriorityMark({ priority }: { priority: string }) {
  if (priority === 'high') {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-600 dark:text-red-400 whitespace-nowrap">
        <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
        high
      </span>
    );
  }
  if (priority === 'low') {
    return <span className="text-[11px] text-dark-muted whitespace-nowrap">low</span>;
  }
  return null;
}

/** Profile photo with an initials fallback (counterpart name or email). */
export function TriageAvatar({
  name,
  imageUrl,
  size = 8,
}: {
  name: string | null;
  imageUrl: string | null;
  size?: 6 | 8 | 10;
}) {
  const sizeClass = size === 6 ? 'w-6 h-6 text-[10px]' : size === 10 ? 'w-10 h-10 text-sm' : 'w-8 h-8 text-xs';
  if (imageUrl) {
    return (
      <img
        src={imageUrl}
        alt={name ?? ''}
        className={`${sizeClass} rounded-full object-cover shrink-0`}
      />
    );
  }
  const initials =
    (name ?? '?')
      .split(/[\s@.]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join('') || '?';
  return (
    <span
      className={`${sizeClass} rounded-full shrink-0 inline-flex items-center justify-center font-semibold bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300`}
    >
      {initials}
    </span>
  );
}
